import React, { useEffect, useLayoutEffect, useRef, useState } from 'react';

// ---------------------------------------------------------------------------
// TailorWiz LinkedIn Profile Package — the branded 10-page client deliverable.
// Faithful port of the design handoff (Evonne Torrence LinkedIn Profile v2).
// High fidelity: colors, type, sizes, spacing and page layout are final.
// Pages are 1275x1650 CSS px (US Letter at 150dpi). Footer bar is ALWAYS 39px
// and nothing may touch it. Experience roles flow onto extra pages; page
// numbers and the page 2 table of contents are computed AFTER pagination.
// Export: screenshot each page at 2x, assemble an image-based Letter PDF.
// Never browser print.
// ---------------------------------------------------------------------------

export interface LinkedInPackageData {
  client: { fullName: string; firstName: string; photoUrl?: string; preparedDate: string };
  basics: { firstName: string; lastName: string; url: string };
  headlines: Array<{ label: string; text: string; blurb: string }>;
  visualBranding: { photo: string; banner: string; customUrl: string };
  about: { blurb: string; paragraphs: string[] };
  experienceBlurb: string;
  experience: Array<{ title: string; company: string; location: string; dates: string; paragraphs: string[]; skilledIn: string }>;
  skills: { blurb: string; items: string[] };
  hashtags: { blurb: string; tags: string[] };
  featuredBlurb: string;
  featured: Array<{ title: string; note: string }>;
  recommendationBlurb: string;
  recommendationScripts: Array<{ label: string; text: string }>;
  credentialsBlurb: string;
  credentials: Array<{ label: string; detail: string }>;
  postsBlurb: string;
  posts: Array<{ title: string; note: string }>;
  volunteerNote?: string;
  basicsBlurb?: string;
  visualBrandingBlurb?: string;
}

// ---- copy rules, enforced at render ---------------------------------------
function cleanCopy(s: string): string {
  return String(s ?? '')
    .replace(/(\d)\s*[–—]\s*(\d)/g, '$1 to $2')
    .replace(/\s*[—]\s*/g, ', ')
    .replace(/\s*[–]\s*/g, ' - ')
    .replace(/\b0(\d)\b/g, '$1');
}

export function validateLinkedInPackage(d: LinkedInPackageData): string[] {
  const v: string[] = [];
  const all = JSON.stringify(d);
  if (/[—]/.test(all)) v.push('em dash found in package data');
  d.headlines.forEach((h, i) => {
    if (h.text.length > 220) v.push(`headline ${i + 1} is ${h.text.length} chars (limit 220)`);
  });
  const aboutChars = d.about.paragraphs.join('\n\n').length;
  if (aboutChars > 2600) v.push(`About is ${aboutChars} chars (limit 2600)`);
  if (/\b0\d\b/.test(all)) v.push('zero-padded number found');
  return v;
}

// ---- design tokens ---------------------------------------------------------
const NAVY = '#244061';
const HL_BG = '#FFF4C2';
const HL_BORDER = '#E6CF6E';
const BODY = '#262626';
const GRAY = '#595959';

const FONT_HREF = 'https://fonts.googleapis.com/css2?family=Source+Serif+4:ital,opsz,wght@0,8..60,400;0,8..60,600;0,8..60,700;1,8..60,400;1,8..60,600&display=swap';
function ensureFontLink() {
  if (!document.querySelector(`link[href="${FONT_HREF}"]`)) {
    const link = document.createElement('link');
    link.rel = 'stylesheet';
    link.href = FONT_HREF;
    document.head.appendChild(link);
  }
}

const FAMILY = "'Source Serif 4', 'Palatino Linotype', serif";

// Experience density presets. The reference itself runs comfortable on its
// first experience page and tight on the fuller ones.
const DENSITY = {
  comfortable: { titlePx: 30, metaPx: 21.5, bodyPx: 22, lh: 1.55, gap: 19, padV: 18, padH: 22, blockGap: 8, sectionGap: 24 },
  tight: { titlePx: 29, metaPx: 21, bodyPx: 21, lh: 1.47, gap: 12, padV: 12, padH: 18, blockGap: 6, sectionGap: 22 },
} as const;
type Density = keyof typeof DENSITY;

// ---- shared building blocks ------------------------------------------------
const SectionTitle: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <div style={{ display: 'flex', alignItems: 'stretch' }}>
    <div style={{ flex: 'none', background: '#eef2f7', border: `1px solid ${NAVY}`, padding: '8px 22px 8px 16px', fontSize: 27, fontWeight: 700, letterSpacing: '0.02em', color: NAVY, whiteSpace: 'nowrap' }}>{children}</div>
    <div style={{ flex: 1, alignSelf: 'center', height: 2, background: `linear-gradient(to right, ${NAVY}, rgba(36,64,97,0))` }} />
  </div>
);

const Blurb: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <div style={{ fontSize: 20, fontStyle: 'italic', lineHeight: 1.5, color: GRAY, textWrap: 'pretty' as any }}>
    <b style={{ fontWeight: 700, color: NAVY, fontStyle: 'italic' }}>What this is:</b> {children}
  </div>
);

const Divider: React.FC = () => <div style={{ height: 1, background: '#e3e8ee', flex: 'none' }} />;

const InlineHl: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <span style={{ background: HL_BG, padding: '1px 8px', boxDecorationBreak: 'clone' as any, WebkitBoxDecorationBreak: 'clone' as any }}>{children}</span>
);

const PageShell: React.FC<{ pageNo: number; fullName: string; padTop?: number; gap: number; children: React.ReactNode; pageKey: string }> = ({ pageNo, fullName, padTop = 30, gap, children, pageKey }) => (
  <div data-li-page={pageKey} style={{ position: 'relative', width: 1275, height: 1650, overflow: 'hidden', background: '#ffffff', flex: 'none', display: 'flex', flexDirection: 'column', fontFamily: FAMILY, lineHeight: 'normal' }}>
    <div style={{ flex: 'none', background: NAVY, padding: '22px 70px 20px', display: 'flex', alignItems: 'flex-end', justifyContent: 'space-between' }}>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 5 }}>
        <div style={{ fontSize: 30, fontWeight: 700, color: '#ffffff', lineHeight: 1 }}>{fullName}</div>
        <div style={{ fontSize: 21.5, fontStyle: 'italic', color: 'rgba(255,255,255,0.85)' }}>LinkedIn Profile Package</div>
      </div>
      <div style={{ fontSize: 21.5, fontStyle: 'italic', color: 'rgba(255,255,255,0.85)' }}>Page {pageNo}</div>
    </div>
    <div data-li-content style={{ flex: 1, minHeight: 0, padding: `${padTop}px 70px 0`, display: 'flex', flexDirection: 'column', gap }}>
      {children}
    </div>
    <div style={{ flex: 'none', height: 39, background: NAVY, marginTop: 20 }} />
  </div>
);

// ---- experience role block -------------------------------------------------
const RoleBlock: React.FC<{ role: LinkedInPackageData['experience'][number]; d: Density }> = ({ role, d }) => {
  const c = DENSITY[d];
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: c.blockGap }}>
      <div style={{ fontSize: c.titlePx, fontWeight: 700, color: NAVY }}>{cleanCopy(role.title)}</div>
      <div style={{ fontSize: c.metaPx, fontWeight: 600, color: GRAY }}>
        {cleanCopy(role.company)}{role.location ? <> &nbsp;&middot;&nbsp; {cleanCopy(role.location)}</> : null} &nbsp;&middot;&nbsp; {cleanCopy(role.dates)}
      </div>
      <div style={{ display: 'flex', flexDirection: 'column', gap: c.gap, fontSize: c.bodyPx, lineHeight: c.lh, color: BODY, textWrap: 'pretty' as any, marginTop: d === 'comfortable' ? 6 : 4, background: HL_BG, border: `1px solid ${HL_BORDER}`, padding: `${c.padV}px ${c.padH}px` }}>
        {role.paragraphs.map((p, i) => <div key={i}>{cleanCopy(p)}</div>)}
        {role.skilledIn ? <div style={{ fontSize: d === 'comfortable' ? 20.5 : 20, fontStyle: 'italic', color: GRAY }}>{cleanCopy(role.skilledIn)}</div> : null}
      </div>
    </div>
  );
};

// ---- pagination ------------------------------------------------------------
interface ExpPageModel { roles: number[]; density: Density; withIntro: boolean }

function usePagination(data: LinkedInPackageData) {
  const measureRef = useRef<HTMLDivElement>(null);
  const [expPages, setExpPages] = useState<ExpPageModel[] | null>(null);

  useLayoutEffect(() => {
    const el = measureRef.current;
    if (!el) return;
    let cancelled = false;
    const run = async () => {
      try { await (document as any).fonts?.ready; } catch { /* measure anyway */ }
      if (cancelled) return;
      // Measured heights of each role at each density, plus intro (title+blurb).
      const q = (sel: string) => Array.from(el.querySelectorAll<HTMLElement>(sel));
      const comfortable = q('[data-m="c"]').map((n) => n.offsetHeight);
      const tight = q('[data-m="t"]').map((n) => n.offsetHeight);
      const introH = el.querySelector<HTMLElement>('[data-m="intro"]')?.offsetHeight ?? 160;
      const headerH = el.querySelector<HTMLElement>('[data-m="header"]')?.offsetHeight ?? 98;
      // Content budget per page: everything above the 39px footer BAR. The
      // reference itself runs its fullest page at ~4px clearance, consuming the
      // footer's 20px top margin; the hard rule is only that nothing touches
      // the bar (clearance >= 0), which the post-render check enforces.
      const budget = 1650 - headerH - 30 - 39;
      const DIV = 1 + 0; // divider height; page gap handles spacing

      const pages: ExpPageModel[] = [];
      let i = 0;
      let first = true;
      while (i < data.experience.length) {
        const cap = budget - (first ? introH + 24 : 0);
        // Greedy fill at comfortable, fall back to tight for this page when the
        // comfortable packing cannot place even one role.
        const pack = (heights: number[], gap: number) => {
          let used = 0; const idx: number[] = [];
          for (let j = i; j < data.experience.length; j++) {
            const h = heights[j] + (idx.length ? DIV + gap * 2 : 0);
            if (used + h > cap) break;
            used += h; idx.push(j);
          }
          return idx;
        };
        let density: Density = 'comfortable';
        let idx = pack(comfortable, DENSITY.comfortable.sectionGap);
        const tightIdx = pack(tight, DENSITY.tight.sectionGap);
        // The reference runs comfortable only when it costs nothing: whenever
        // tight fits MORE roles on this page, take tight (fewer total pages).
        if (tightIdx.length > idx.length) { density = 'tight'; idx = tightIdx; }
        if (idx.length === 0) { density = 'tight'; idx = [i]; } // oversized single role: honest overflow, flagged below
        pages.push({ roles: idx, density, withIntro: first });
        i = idx[idx.length - 1] + 1;
        first = false;
      }
      if (!cancelled) setExpPages(pages);
    };
    void run();
    return () => { cancelled = true; };
  }, [data]);

  return { measureRef, expPages };
}

// ---- main component --------------------------------------------------------
interface Props {
  data: LinkedInPackageData;
  onFitResult?: (r: { ok: boolean; pageCount: number; overflows: string[] }) => void;
}

export const LinkedInProfilePackage: React.FC<Props> = ({ data, onFitResult }) => {
  const { measureRef, expPages } = usePagination(data);
  const rootRef = useRef<HTMLDivElement>(null);

  useEffect(() => { ensureFontLink(); }, []);

  // Post-layout fit check: nothing may touch the footer bar.
  useEffect(() => {
    if (!expPages || !rootRef.current) return;
    const root = rootRef.current;
    let cancelled = false;
    const check = async () => {
      try { await (document as any).fonts?.ready; } catch { /* proceed */ }
      if (cancelled) return;
      const overflows: string[] = [];
      root.querySelectorAll<HTMLElement>('[data-li-page]').forEach((page) => {
        const content = page.querySelector<HTMLElement>('[data-li-content]');
        if (!content) return;
        const pageRect = page.getBoundingClientRect();
        const scale = pageRect.height / 1650 || 1;
        const footerTop = pageRect.top + (1650 - 39) * scale;
        let lowest = 0;
        content.querySelectorAll<HTMLElement>('*').forEach((n) => {
          const b = n.getBoundingClientRect().bottom;
          if (b > lowest) lowest = b;
        });
        const clearance = (footerTop - lowest) / scale;
        if (clearance < 0) overflows.push(`${page.dataset.liPage}: content over footer by ${Math.round(-clearance)}px`);
      });
      onFitResult?.({ ok: overflows.length === 0, pageCount: root.querySelectorAll('[data-li-page]').length, overflows });
    };
    void check();
    return () => { cancelled = true; };
  }, [expPages, data, onFitResult]);

  const fullName = cleanCopy(data.client.fullName);
  const expPageCount = expPages?.length ?? 1;
  const pExpFirst = 5;
  const pSkills = pExpFirst + expPageCount;
  const pFeatured = pSkills + 1;
  const pCreds = pFeatured + 1;

  const toc: Array<{ title: string; page: number }> = [
    { title: 'Profile Basics', page: 3 },
    { title: `Headline · ${cleanCopy(data.headlines[0]?.label ?? 'Option 1')}`, page: 3 },
    { title: `Headline · ${cleanCopy(data.headlines[1]?.label ?? 'Option 2')}`, page: 3 },
    { title: 'Visual Branding', page: 3 },
    { title: 'About Section', page: 4 },
    { title: 'Professional Experience', page: pExpFirst },
    { title: `Skills · All ${data.skills.items.length}`, page: pSkills },
    { title: `Hashtag Strategy · ${data.hashtags.tags.length} Tags`, page: pSkills },
    { title: 'Featured Section', page: pFeatured },
    { title: 'Recommendation Request Scripts', page: pFeatured },
    { title: 'Certifications, Education & Accomplishments', page: pCreds },
    { title: `Activity Strategy · Your First ${data.posts.length} Posts`, page: pCreds },
  ];

  return (
    <div ref={rootRef} style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 28, fontFamily: FAMILY, lineHeight: 'normal' }}>

      {/* Hidden measurer: role blocks at both densities + intro + header probe */}
      <div ref={measureRef} style={{ position: 'fixed', left: -20000, top: 0, width: 1275, visibility: 'hidden', pointerEvents: 'none', lineHeight: 'normal' }} aria-hidden>
        <div data-m="header" style={{ background: NAVY, padding: '22px 70px 20px', display: 'flex', alignItems: 'flex-end', justifyContent: 'space-between' }}>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 5 }}>
            <div style={{ fontSize: 30, fontWeight: 700, lineHeight: 1 }}>{fullName}</div>
            <div style={{ fontSize: 21.5, fontStyle: 'italic' }}>LinkedIn Profile Package</div>
          </div>
          <div style={{ fontSize: 21.5 }}>Page 5</div>
        </div>
        <div style={{ padding: '0 70px' }}>
          <div data-m="intro" style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
            <SectionTitle>Professional Experience</SectionTitle>
            <Blurb>{cleanCopy(data.experienceBlurb)}</Blurb>
          </div>
          {data.experience.map((r, i) => <div key={`c${i}`} data-m="c"><RoleBlock role={r} d="comfortable" /></div>)}
          {data.experience.map((r, i) => <div key={`t${i}`} data-m="t"><RoleBlock role={r} d="tight" /></div>)}
        </div>
      </div>

      {/* PAGE 1: COVER */}
      <div data-li-page="cover" style={{ position: 'relative', width: 1275, height: 1650, overflow: 'hidden', background: NAVY, flex: 'none', fontFamily: FAMILY, lineHeight: 'normal' }}>
        <div style={{ position: 'absolute', left: 80, top: 70, fontSize: 26, fontWeight: 700, color: '#ffffff' }}>Jobs On <span style={{ fontStyle: 'italic' }}>Demand</span></div>
        <div style={{ position: 'absolute', right: 80, top: 74, fontSize: 16, fontWeight: 700, letterSpacing: '0.22em', textTransform: 'uppercase', color: 'rgba(255,255,255,0.5)' }}>Client Deliverable</div>
        <div style={{ position: 'absolute', left: 80, right: 80, top: 400, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 34 }}>
          {data.client.photoUrl ? (
            <div style={{ padding: 10, background: '#ffffff', border: '3px solid #14183a', boxShadow: '0 14px 34px rgba(6,10,26,0.45)' }}>
              <img src={data.client.photoUrl} alt={fullName} crossOrigin="anonymous" style={{ display: 'block', width: 320, height: 330, objectFit: 'cover', objectPosition: '50% 18%' }} />
            </div>
          ) : null}
          <div style={{ fontSize: 72, fontWeight: 700, letterSpacing: '-0.01em', lineHeight: 1.05, color: '#ffffff', textAlign: 'center' }}>
            Your Complete<br />LinkedIn <span style={{ fontStyle: 'italic', fontWeight: 400 }}>Profile Package</span>
          </div>
          <div style={{ fontSize: 27, fontWeight: 600, color: 'rgba(255,255,255,0.85)' }}>
            Prepared for <span style={{ color: '#ffffff', fontWeight: 700 }}>{fullName}</span> &nbsp;&middot;&nbsp; {cleanCopy(data.client.preparedDate)}
          </div>
        </div>
        <div style={{ position: 'absolute', left: 80, right: 80, bottom: 54, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <div style={{ fontSize: 17, fontWeight: 600, color: 'rgba(255,255,255,0.5)' }}>www.executivejobsondemand.com &nbsp;&middot;&nbsp; Confidential</div>
          <div style={{ fontSize: 19, fontWeight: 700, color: 'rgba(255,255,255,0.45)' }}>1</div>
        </div>
      </div>

      {/* PAGE 2: HOW TO USE + TOC */}
      <PageShell pageKey="howto" pageNo={2} fullName={fullName} padTop={40} gap={34}>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 22 }}>
          <SectionTitle>How to Use This Package</SectionTitle>
          <div style={{ fontSize: 22, lineHeight: 1.55, color: BODY, textWrap: 'pretty' as any }}>
            Everything in this package is written and ready to go. You do not need to rewrite anything. Just copy and paste each section directly into the matching section of your LinkedIn profile.
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 18 }}>
            {[
              { n: 1, t: 'Find the yellow highlight', d: <>Everything you need to copy is highlighted in light yellow, like <InlineHl>this</InlineHl>. If it is not highlighted, it is guidance for you, not profile text.</> },
              { n: 2, t: 'Copy it exactly as written', d: <>Select the highlighted text and copy it word for word. Keywords, character counts, and order are already set for recruiter searches.</> },
              { n: 3, t: 'Paste into the matching section', d: <>Open that same section on your LinkedIn profile, paste, and save. Then move to the next one.</> },
            ].map((s) => (
              <div key={s.n} style={{ display: 'flex', gap: 20, alignItems: 'flex-start' }}>
                <div style={{ flex: 'none', width: 46, height: 46, border: `2px solid ${NAVY}`, color: NAVY, fontSize: 22, fontWeight: 700, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>{s.n}</div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
                  <div style={{ fontSize: 23, fontWeight: 700, color: BODY }}>{s.t}</div>
                  <div style={{ fontSize: 20.5, lineHeight: 1.5, color: GRAY, textWrap: 'pretty' as any }}>{s.d}</div>
                </div>
              </div>
            ))}
          </div>
        </div>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
          <SectionTitle>Inside This Package</SectionTitle>
          <div style={{ display: 'flex', flexDirection: 'column', borderTop: '1px solid #d5dce6' }}>
            {toc.map((row, i) => (
              <div key={i} style={{ display: 'flex', alignItems: 'baseline', gap: 16, padding: '15px 0', borderBottom: '1px solid #d5dce6' }}>
                <div style={{ fontSize: 25, fontWeight: 700, color: NAVY }}>{row.title}</div>
                <div style={{ flex: 1, borderBottom: '2px dotted #b9c4d3', transform: 'translateY(-6px)' }} />
                <div style={{ fontSize: 22, fontStyle: 'italic', color: GRAY }}>Page {row.page}</div>
              </div>
            ))}
          </div>
        </div>
      </PageShell>

      {/* PAGE 3: BASICS + HEADLINES + VISUAL BRANDING */}
      <PageShell pageKey="basics" pageNo={3} fullName={fullName} gap={35}>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
          <SectionTitle>Profile Basics</SectionTitle>
          <Blurb>{cleanCopy(data.basicsBlurb ?? 'the foundation fields of your profile. The name field carries an extra keyword tag so recruiters searching your target title find you by name match, not just headline match.')}</Blurb>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8, fontSize: 22, lineHeight: 1.55, color: BODY }}>
            <div><b style={{ fontWeight: 700, color: NAVY }}>First Name:</b> <InlineHl>{cleanCopy(data.basics.firstName)}</InlineHl></div>
            <div><b style={{ fontWeight: 700, color: NAVY }}>Last Name:</b> <InlineHl>{cleanCopy(data.basics.lastName)}</InlineHl></div>
            <div><b style={{ fontWeight: 700, color: NAVY }}>LinkedIn URL:</b> <InlineHl>{cleanCopy(data.basics.url)}</InlineHl></div>
          </div>
        </div>
        <Divider />
        {data.headlines.slice(0, 2).map((h, i) => (
          <div key={i} style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
            <SectionTitle>Headline &middot; {cleanCopy(h.label)}</SectionTitle>
            <Blurb>{cleanCopy(h.blurb)}</Blurb>
            <div style={{ border: `1px solid ${NAVY}`, background: HL_BG, padding: '20px 26px', fontSize: 23.5, fontWeight: 600, lineHeight: 1.55, color: BODY, textWrap: 'pretty' as any }}>{cleanCopy(h.text)}</div>
          </div>
        ))}
        <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
          <SectionTitle>Visual Branding</SectionTitle>
          <Blurb>{cleanCopy(data.visualBrandingBlurb ?? 'the three visual decisions that make your profile and resume read as one consistent brand when a recruiter opens both.')}</Blurb>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 14, fontSize: 22, lineHeight: 1.5, color: BODY }}>
            <div><b style={{ fontWeight: 700, color: NAVY }}>Profile photo:</b> {cleanCopy(data.visualBranding.photo)}</div>
            <div><b style={{ fontWeight: 700, color: NAVY }}>Banner:</b> {cleanCopy(data.visualBranding.banner)}</div>
            <div><b style={{ fontWeight: 700, color: NAVY }}>Custom URL:</b> {cleanCopy(data.visualBranding.customUrl)}</div>
          </div>
        </div>
      </PageShell>

      {/* PAGE 4: ABOUT */}
      <PageShell pageKey="about" pageNo={4} fullName={fullName} gap={19}>
        <SectionTitle>About Section</SectionTitle>
        <Blurb>{cleanCopy(data.about.blurb)}</Blurb>
        <div style={{ border: `1px solid ${NAVY}`, background: HL_BG, padding: '26px 32px', display: 'flex', flexDirection: 'column', gap: 22, fontSize: 22, lineHeight: 1.58, color: BODY, textWrap: 'pretty' as any }}>
          {data.about.paragraphs.map((p, i) => <div key={i}>{cleanCopy(p)}</div>)}
        </div>
      </PageShell>

      {/* EXPERIENCE PAGES (paginated) */}
      {(expPages ?? [{ roles: data.experience.map((_, i) => i), density: 'comfortable' as Density, withIntro: true }]).map((page, pi) => (
        <PageShell key={`exp${pi}`} pageKey={`exp${pi + 1}`} pageNo={pExpFirst + pi} fullName={fullName} gap={DENSITY[page.density].sectionGap}>
          {page.withIntro && (
            <>
              <SectionTitle>Professional Experience</SectionTitle>
              <Blurb>{cleanCopy(data.experienceBlurb)}</Blurb>
            </>
          )}
          {page.roles.map((ri, k) => (
            <React.Fragment key={ri}>
              {k > 0 && <Divider />}
              <RoleBlock role={data.experience[ri]} d={page.density} />
            </React.Fragment>
          ))}
        </PageShell>
      ))}

      {/* SKILLS + HASHTAGS */}
      <PageShell pageKey="skills" pageNo={pSkills} fullName={fullName} gap={32}>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
          <SectionTitle>Skills &middot; All {data.skills.items.length}</SectionTitle>
          <Blurb>{cleanCopy(data.skills.blurb)}</Blurb>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
            {data.skills.items.map((s, i) => (
              <div key={i} style={{ border: `1px solid ${HL_BORDER}`, background: HL_BG, padding: '6px 14px', fontSize: 18.5, fontWeight: 600, color: NAVY }}>{cleanCopy(s)}</div>
            ))}
          </div>
        </div>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
          <SectionTitle>Hashtag Strategy &middot; {data.hashtags.tags.length} Tags</SectionTitle>
          <Blurb>{cleanCopy(data.hashtags.blurb)}</Blurb>
          <div style={{ fontSize: 21.5, fontWeight: 600, lineHeight: 1.7, color: NAVY, textWrap: 'pretty' as any }}>
            {data.hashtags.tags.map((t, i) => <React.Fragment key={i}>{cleanCopy(t)}{i < data.hashtags.tags.length - 1 ? <> &nbsp;</> : null}</React.Fragment>)}
          </div>
        </div>
      </PageShell>

      {/* FEATURED + RECOMMENDATION SCRIPTS */}
      <PageShell pageKey="featured" pageNo={pFeatured} fullName={fullName} gap={30}>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
          <SectionTitle>Featured Section</SectionTitle>
          <Blurb>{cleanCopy(data.featuredBlurb)}</Blurb>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 9, fontSize: 21.5, lineHeight: 1.5, color: BODY }}>
            {data.featured.map((f, i) => (
              <div key={i}><b style={{ fontWeight: 700, color: NAVY }}>{cleanCopy(f.title)}</b> - {cleanCopy(f.note)}</div>
            ))}
          </div>
        </div>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
          <SectionTitle>Recommendation Request Scripts</SectionTitle>
          <Blurb>{cleanCopy(data.recommendationBlurb)}</Blurb>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 16, fontSize: 21, lineHeight: 1.5, color: BODY }}>
            {data.recommendationScripts.map((r, i) => (
              <div key={i} style={{ border: `1px solid ${NAVY}`, background: HL_BG, padding: '16px 22px' }}>
                <b style={{ fontWeight: 700, color: NAVY }}>{i + 1} &middot; {cleanCopy(r.label)}:</b> {cleanCopy(r.text)}
              </div>
            ))}
          </div>
        </div>
      </PageShell>

      {/* CREDENTIALS + POSTS + VOLUNTEER */}
      <PageShell pageKey="creds" pageNo={pCreds} fullName={fullName} gap={30}>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
          <SectionTitle>Certifications, Education &amp; Accomplishments</SectionTitle>
          <Blurb>{cleanCopy(data.credentialsBlurb)}</Blurb>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8, fontSize: 21.5, lineHeight: 1.5, color: BODY, background: HL_BG, border: `1px solid ${HL_BORDER}`, padding: '18px 22px' }}>
            {data.credentials.map((c, i) => (
              <div key={i}><b style={{ fontWeight: 700, color: NAVY }}>{cleanCopy(c.label)}</b>{c.label.trim().endsWith(':') ? ' ' : ' - '}{cleanCopy(c.detail)}</div>
            ))}
          </div>
        </div>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
          <SectionTitle>Activity Strategy &middot; Your First {data.posts.length} Posts</SectionTitle>
          <Blurb>{cleanCopy(data.postsBlurb)}</Blurb>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 14, fontSize: 21.5, lineHeight: 1.5, color: BODY }}>
            {data.posts.map((p, i) => (
              <div key={i}><b style={{ fontWeight: 700, color: NAVY }}>{i + 1} &middot; {cleanCopy(p.title)}</b> {cleanCopy(p.note)}</div>
            ))}
          </div>
        </div>
        {data.volunteerNote ? (
          <div style={{ borderTop: '1px solid #e3e8ee', paddingTop: 14, fontSize: 20.5, lineHeight: 1.5, color: GRAY, textWrap: 'pretty' as any }}>
            <b style={{ fontWeight: 700, color: NAVY }}>Volunteer experience:</b> {cleanCopy(data.volunteerNote)}
          </div>
        ) : null}
      </PageShell>
    </div>
  );
};

/**
 * Export every rendered package page to one image-based US Letter PDF.
 * Screenshot each page at 2x (2550x3300), JPEG ~0.92, never browser print.
 * File name: `<Client Name> - LinkedIn Profile Package.pdf`.
 */
export async function exportLinkedInPackagePdf(container: HTMLElement, fullName: string): Promise<void> {
  const [{ default: html2canvas }, { default: jsPDF }] = await Promise.all([
    import('html2canvas'),
    import('jspdf'),
  ]);
  try { await (document as any).fonts?.ready; } catch { /* proceed */ }
  const pages = Array.from(container.querySelectorAll<HTMLElement>('[data-li-page]'));
  if (!pages.length) throw new Error('no package pages rendered');
  const pdf = new jsPDF({ orientation: 'portrait', unit: 'pt', format: 'letter' });
  for (let i = 0; i < pages.length; i++) {
    const canvas = await html2canvas(pages[i], { scale: 2, width: 1275, height: 1650, backgroundColor: '#ffffff', useCORS: true, logging: false });
    const img = canvas.toDataURL('image/jpeg', 0.92);
    if (i > 0) pdf.addPage('letter', 'portrait');
    pdf.addImage(img, 'JPEG', 0, 0, 612, 792);
  }
  pdf.save(`${fullName.trim()} - LinkedIn Profile Package.pdf`);
}
